/** @jsx jsx */

import Icon from '@zapier/common-components/src/components/Icon';
import { Border, Colors, Typography } from '@zapier/theme';
import { css, jsx } from '@emotion/core';
import { marginToCss } from '@zapier/common-utils';

type Spacing = any; // Remove this and import Spacing from @zapier/common-utils after our version of TypeScript supports the `import { type Spacing }` syntax.

// MessageList ================================================================

type MessageListProps = {
  isOrdered?: boolean;
  children: React.ReactNode;
};

const messageListCss = (isOrdered: boolean) =>
  css({
    paddingLeft: '1em',
    marginBottom: '0.5em',
    listStyleType: isOrdered ? 'decimal' : 'disc',
    ':last-child': {
      marginBottom: 0,
    },
  });

const MessageList: React.FC<MessageListProps> = ({
  children,
  isOrdered = false,
}) => {
  const Tag = isOrdered ? 'ol' : 'ul';
  return <Tag css={messageListCss(isOrdered)}>{children}</Tag>;
};

MessageList.defaultProps = {
  isOrdered: false,
};

// MessageText ================================================================

const messageTextCss = css({
  lineHeight: 'inherit',
  margin: '1em 0',
  ':last-child': {
    marginBottom: 0,
  },
});

const MessageText = (props: MessageBarProps) => (
  <div css={messageTextCss}>{props.children}</div>
);

// MessageBar =================================================================

export type MessageBarTypes =
  | 'default'
  | 'error'
  | 'info'
  | 'plain'
  | 'success'
  | 'teams'
  | 'warning';

type BorderRadiusTypes = keyof typeof Border.radius;

const TYPES_MAP = {
  default: {
    defaultTitle: '',
    icon: null,
    styles: {
      backgroundColor: Colors.storm,
      borderColor: Colors.batmobile,
    },
  },

  error: {
    defaultTitle: 'Bargle!',
    icon: 'error',
    styles: {
      backgroundColor: Colors.status.error.bg,
      borderColor: Colors.status.error.border,
    },
  },

  info: {
    defaultTitle: '',
    icon: null,
    styles: {
      backgroundColor: Colors.iceman,
      borderColor: Colors.captainAmerica,
    },
  },

  plain: {
    defaultTitle: '',
    icon: null,
    styles: {
      backgroundColor: Colors.status.plain.bg,
      borderColor: Colors.status.plain.border,
    },
  },

  success: {
    defaultTitle: 'Success!',
    icon: 'check',
    styles: {
      backgroundColor: Colors.status.success.bg,
      borderColor: Colors.status.success.border,
    },
  },

  teams: {
    defaultTitle: 'Team!',
    icon: 'team',
    iconStyle: {
      backgroundColor: 'transparent',
      borderRadius: 0,
      fill: Colors.mystique,
    },
    styles: {
      backgroundColor: Colors.thunder,
      borderColor: Colors.mystique,
    },
  },

  warning: {
    defaultTitle: 'Warning.',
    icon: 'alert',
    styles: {
      backgroundColor: Colors.status.warning.bg,
      borderColor: Colors.status.warning.border,
    },
  },
};

type MessageBarProps = {
  /** The border radius. Accepts any of the keys from `Border.radius` from `@zapier/theme`. */
  borderRadius?: BorderRadiusTypes;
  /** Children (usually text) to display as the main content of the message. */
  children?: React.ReactNode;
  /** Prevents an icon from being rendered. */
  hideIcon?: boolean;
  /** Icon name (will override the default icon for the type) */
  icon?: string;
  /** Does not allow the bar to overflow screen on mobile (for toasts) */
  fitToScreen?: boolean;
  /** Handler for dismissing the message. */
  onDismiss?: () => void;
  margin?: Spacing;
  /** Bolded title to display in the header. */
  title?: string;
  /** The type of message bar to display. */
  type: MessageBarTypes;
  /**
   * The type (effectively the "style") of text to render. One of a list of
   * preset types, which correspond exactly to the Zapier Visual Identity:
   * https://github.com/zapier/zapier-ui/issues/36. These styles are mixed
   * in to the MessageBar styles, overriding any existing MessageBar styles
   * specified by the MessageBar type.
   */
  typography?: keyof typeof Typography; // Use the TypographyName type from @zapier/theme instead after our version of TypeScript supports the `import { type TypographyName }` syntax.
};

type MessageBarSubComponents = {
  MessageList: typeof MessageList;
  MessageText: typeof MessageText;
};

const messageBarBaseCss = (
  borderRadius: BorderRadiusTypes,
  margin: Spacing | undefined | null,
  fitToScreen: boolean
) =>
  css({
    ...marginToCss(margin, '20px'),
    borderRadius: Border.radius[borderRadius] || Border.radius.default,
    borderStyle: 'solid',
    borderWidth: 1,
    color: Colors.deepSpace,
    display: 'flex',
    fontSize: 16,
    maxWidth: fitToScreen ? '90vw' : 'unset',
    padding: 15,
    position: 'relative',
    transition: 'background-color 300ms',
    '& a': {
      color: 'inherit',
    },
  });

const messageCss = css({
  flex: 1,
  lineHeight: 1.6,
  minWidth: 0,
});

const iconBaseCss = css({
  backgroundColor: Colors.white,
  borderRadius: '50%',
  marginRight: 8,
  marginTop: 3,
});

const dismissCss = css({
  marginLeft: 8,
  marginTop: 3,
});

const titleCss = css({
  fontWeight: 600,
});

/**
 * Displays a bar with useful information for the user.
 *
 * Comes bundled with sub-components:
 * - `<MessageBar.MessageText>`: Used for displaying additional sub-text.
 * - `<MessageBar.MessageList>`: Used with lists.
 */
const MessageBar: React.FC<MessageBarProps> &
  MessageBarSubComponents = props => {
  const type = TYPES_MAP[props.type];
  const typographyStyles = props.typography ? Typography[props.typography] : {};

  const messageBarCss = css(
    messageBarBaseCss(props.borderRadius!, props.margin, props.fitToScreen!),
    type.styles,
    typographyStyles
  );

  const iconCss = css(
    iconBaseCss,
    {
      color: type.styles.borderColor,
    },
    // @ts-ignore
    type.iconStyle
  );

  // Allow user to pass title (or empty title to clear) or fall back to default title for type
  const icon = !props.hideIcon && (props.icon || type.icon);

  const title = props.title !== undefined ? props.title : type.defaultTitle;

  return (
    <div css={messageBarCss}>
      {!!icon && <Icon css={iconCss} name={icon} size={20} />}

      <div css={messageCss}>
        {title && <strong css={titleCss}>{title}</strong>} {props.children}
      </div>

      {!!props.onDismiss && (
        <Icon css={dismissCss} name="delete" onClick={props.onDismiss} />
      )}
    </div>
  );
};

MessageBar.defaultProps = {
  borderRadius: 'default',
  fitToScreen: false,
  hideIcon: false,
  type: 'default',
};

MessageBar.MessageList = MessageList;
MessageBar.MessageText = MessageText;

export default MessageBar;
